import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChannelTagType } from '@televet/shared-types/JsonTypes/ChannelTag';
import { DEFAULT_APPOINTMENT_DURATION_IN_MINUTES } from 'shared/providers/ApolloProvider/reactiveVars/appointmentFilters';
import {
  AppointmentPetFragment,
  AppointmentStatus,
  ChannelStatusAction,
  PimsWritebackStatus,
} from 'shared/types/graphql';
import { StateSliceName } from 'state/StateSliceName';
import { AppointmentError } from '../components/Modals/AppointmentModal/hooks/useAppointmentValidation';

type AppointmentModalMode = 'create' | 'edit' | 'view';

export interface AppointmentDraft {
  clinicEmployeeId?: string;
  roomId?: string;
  petId?: string;
  petParentIds?: string[];
  appointmentTypeId?: string;
  startAt?: string; // ISO Date
  durationInMinutes: number;
  description?: string;
  recapNotes?: string;
  selectedAutomationIds?: string[];
  selectedPet?: AppointmentPetFragment;
  pimsId?: string;
  petParentName?: string;
  petName?: string;
  aiRecapWriteback?: {
    id: string;
    status: PimsWritebackStatus;
    updatedAt: string;
  };
  status?: AppointmentStatus;
  channelStatus?: {
    name: string;
    color: string;
    channelStatusAction: ChannelStatusAction;
  };
  channelId?: string;
  clinicEmployee?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  appointmentType?: {
    id: string;
    name: string;
  };
  room?: {
    id: string;
    name: string;
  };
  tags?: {
    name: string;
    tagType: ChannelTagType;
    colorBackground?: string;
  }[];
}

interface AppointmentModalState {
  isOpen: boolean;
  mode: AppointmentModalMode;
  appointmentId?: string;
  appointmentDraft: AppointmentDraft;
  originalAppointment: AppointmentDraft;
  surveyAutomationIds?: string[];
  errors: AppointmentError[];
}

const initialState: AppointmentModalState = {
  isOpen: false,
  mode: 'create',
  appointmentDraft: {
    durationInMinutes: DEFAULT_APPOINTMENT_DURATION_IN_MINUTES,
  },
  originalAppointment: {
    durationInMinutes: DEFAULT_APPOINTMENT_DURATION_IN_MINUTES,
  },
  errors: [],
};

const appointmentsModalSlice = createSlice({
  name: StateSliceName.AppointmentsModal,
  initialState,
  reducers: {
    updateAppointmentModal: (state, action: PayloadAction<Partial<AppointmentModalState>>) => {
      Object.assign(state, action.payload);
    },
    updateAppointmentDraft: (state, action: PayloadAction<Partial<AppointmentDraft>>) => {
      Object.assign(state.appointmentDraft, action.payload);
    },
    updateAppointmentModalErrors: (state, action: PayloadAction<AppointmentError[]>) => {
      state.errors = action.payload;
    },
    openAppointmentModal: (
      state,
      action: PayloadAction<(Partial<AppointmentDraft> & { appointmentId?: string }) | undefined>,
    ) => {
      Object.assign(state, {
        isOpen: true,
        mode: action.payload?.appointmentId ? 'view' : 'create',
        appointmentId: action.payload?.appointmentId,
        appointmentDraft: {
          ...state.appointmentDraft,
          startAt: action.payload?.startAt,
          selectedPet: action.payload?.selectedPet,
          petId: action.payload?.petId,
          petParentIds: action.payload?.petParentIds,
          petParentName: action.payload?.petParentName,
          petName: action.payload?.petName,
        },
      });
    },
    closeAppointmentModal: () => {
      return initialState;
    },
    setOriginalAppointment: (state, action: PayloadAction<AppointmentDraft>) => {
      state.originalAppointment = action.payload;
    },
  },
});

export const {
  openAppointmentModal,
  closeAppointmentModal,
  updateAppointmentModal,
  updateAppointmentDraft,
  updateAppointmentModalErrors,
  setOriginalAppointment,
} = appointmentsModalSlice.actions;

export default appointmentsModalSlice.reducer;
